'use client';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import Image from 'next/image';
import { useUserContext } from '@osmosis/nextjs-components/context/UserContext';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';
import { bg1, bg2 } from '@osmosis/tailwind-ui/styles/background';
import { text4 } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  DEFAULT_QUOTE_COLORS,
  DEFAULT_SOCIAL_PROOF_TESTIMONIALS,
  SocialProofArgs,
} from '../../lib/composer/schemas/answersValueProp';
import { getFontClass } from '../../lib/composer/schemas/font';

type Testimonial = {
  id: string;
  quote: string;
  name: string;
  role: string;
  image?: string;
  /** Avatar image shown next to the name (falls back to a placeholder circle). */
  avatar?: string;
  /** `text-*` class applied to the quote mark. */
  quoteColor?: string;
  /** Responsive font-size class for the quote body. */
  quoteSize?: string;
  /** Responsive flex-direction class for the avatar/name/role attribution row. */
  attributionLayout?: string;
  /** Font-family class applied to the card's text. */
  fontClass?: string;
};

// Fallback testimonials when no composer config exists.
const TESTIMONIALS: Testimonial[] = DEFAULT_SOCIAL_PROOF_TESTIMONIALS.map(
  (testimonial, index) => ({
    id: `default-${index}`,
    quote: testimonial.quote,
    name: testimonial.name,
    role: testimonial.role,
    image: testimonial.image,
    avatar: testimonial.avatar,
  }),
);

const TestimonialCard = ({
  quote,
  name,
  role,
  image,
  avatar,
  quoteColor,
  quoteSize,
  attributionLayout,
  fontClass,
}: Testimonial) => (
  <>
    {/* The hero image is its own element, separate from the testimonial card. */}
    {image ? (
      <div className="w-full max-w-sm overflow-hidden rounded-lg">
        <Image
          src={image}
          alt={name}
          width={384}
          height={384}
          sizes="384px"
          className="aspect-square w-full object-cover"
        />
      </div>
    ) : null}
    <div
      className={`overflow-hidden rounded-lg ${bg1}`}
      data-testid="answer-testimonial"
    >
      <div className="p-4">
        <p
          className={cn(
            'mb-2 text-3xl font-bold leading-none',
            quoteColor,
            fontClass,
          )}
          aria-hidden="true"
        >
          {/* Decorative quotation mark, not translatable copy. */}
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {'“'}
        </p>
        <p className={cn(`mb-3 ${text4}`, quoteSize, fontClass)}>
          {/* Decorative quotation mark, not translatable copy. */}
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {quote + '"'}
        </p>
        <div className={cn('flex gap-2', attributionLayout)}>
          {avatar ? (
            <Image
              src={avatar}
              alt={name}
              width={64}
              height={64}
              className="h-16 w-16 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div
              className={`h-16 w-16 shrink-0 rounded-full ${bg2}`}
              aria-hidden="true"
            />
          )}
          <div>
            <p className={cn(`text-lg font-bold ${text4}`, fontClass)}>
              {name}
            </p>
            <p className={cn(`text-base ${text4}`, fontClass)}>{role}</p>
          </div>
        </div>
      </div>
    </div>
  </>
);

type Props = {
  /** Editable copy from the composer config; falls back to defaults below. */
  config?: SocialProofArgs;
  /** Renders even when a user is logged in (composer preview only). */
  previewMode?: boolean;
};

/**
 * Social proof / testimonials module shown below the related topics sidebar
 * for logged-out users on answer pages.
 */
const AnswerSocialProof = ({ config, previewMode }: Props) => {
  const { user } = useUserContext();
  const { getClassValue } = useContentSize();

  if (user && !previewMode) return null;

  const baseTestimonials: Testimonial[] = config?.testimonials?.length
    ? config.testimonials.map((testimonial, index) => ({
        id: `config-${index}`,
        quote: testimonial.quote,
        name: testimonial.name,
        role: testimonial.role,
        image: testimonial.image,
        avatar: testimonial.avatar,
        quoteColor: testimonial.quoteColor,
      }))
    : TESTIMONIALS;

  const fontClass = getFontClass(config?.fontStyle, 'font-national-2');

  // Quote body size: 18px (text-lg) at the smallest breakpoint, 20px (text-xl)
  // from `md` up. Container-width driven (see the layout note below).
  const quoteSize = getClassValue({
    defaultClasses: 'text-lg md:text-xl',
    breakpoints: { md: 'text-xl', base: 'text-lg' },
  });

  // Stack the avatar/name/role vertically at the smallest width; align them in a
  // row from `md` up.
  const attributionLayout = getClassValue({
    defaultClasses: 'flex-col items-start md:flex-row md:items-center',
    breakpoints: { md: 'flex-row items-center', base: 'flex-col items-start' },
  });

  const testimonials = baseTestimonials.map((testimonial, index) => ({
    ...testimonial,
    // Composer-selected color wins; otherwise cycle the position defaults.
    quoteColor:
      testimonial.quoteColor ||
      DEFAULT_QUOTE_COLORS[index % DEFAULT_QUOTE_COLORS.length],
    quoteSize,
    attributionLayout,
    fontClass,
  }));

  // Keep the testimonials 2x2 while the social-proof section spans the full
  // content width, and collapse to a single column only once it becomes the
  // narrow right-hand sidebar. That sidebar split is driven by AnswersGrid at
  // the `xl` breakpoint (`block xl:grid`), so this switch must use `xl` too —
  // using `md` here would collapse to a column far too early (~800px).
  //
  // NOTE: `useContentSize` measures the `#ai-panel-content` container (bound in
  // WithAIPanel), not the viewport. That container is narrower than the viewport
  // by the layout shell's horizontal padding (`px-4` = 32px total, plus the
  // scrollbar), so this `xl` = 1280px switch actually fires at ~1314px of
  // viewport width. If the AI rail is open the offset is larger still.
  const testimonialLayout = getClassValue({
    defaultClasses: 'grid-cols-2 xl:max-w-sm xl:grid-cols-1',
    breakpoints: {
      xl: 'max-w-sm grid-cols-1',
      base: 'grid-cols-2',
    },
  });

  return (
    <div
      className="mt-4 flex w-full flex-col items-start gap-5 rounded-lg py-4"
      data-testid="answer-social-proof"
    >
      <div className="flex flex-col gap-2 pl-4">
        <strong
          className={cn(
            `text-3xl font-bold ${text4}`,
            config?.headingFontSize,
            fontClass,
          )}
        >
          {config?.heading ?? (
            <FormattedMessage
              id="answers.socialProof.heading"
              defaultMessage="Students say Osmosis is 100% worth it"
            />
          )}
        </strong>
        <p
          className={cn(`text-lg ${text4}`, config?.subtextFontSize, fontClass)}
        >
          {config?.subtext ?? (
            <FormattedMessage
              id="answers.socialProof.subtext"
              defaultMessage="Because Osmosis saves them time. Lowers stress. And actually helps them remember when it counts."
            />
          )}
        </p>
      </div>
      <div
        className={cn('grid w-full gap-4', testimonialLayout)}
        data-testid={'testimonial-container'}
      >
        {testimonials.map((testimonial) => (
          <TestimonialCard key={testimonial.id} {...testimonial} />
        ))}
      </div>
    </div>
  );
};

export default AnswerSocialProof;
